import axios from 'axios';
import { authHeaders } from './authHeaders';
import {
  Alta,
  Ejecutiva,
  FiltroEstadoAlta,
  DecisionRevisar,
  ConsultaDni,
  ReporteVentas,
  CreateAltaPayload,
  UpdateAltaPayload,
  MutationResponse,
  FilaImportar,
  ImportarResponse,
  Sincronizacion,
  VentasResponse,
  OrdenAltas,
} from '../types/Altas';

const API_URL = '/api/altas';

export interface FiltrosAltas {
  ejecutivaId?: number;
  estado?: FiltroEstadoAlta;
  /** Por fecha de venta o de carga; el backend usa 'venta_desc' si no viene. */
  orden?: OrdenAltas;
  /** Nombre (sin acentos) o DNI. */
  q?: string;
  limit?: number;
  offset?: number;
}

/**
 * Texto para mostrar: el del backend si vino, si no el del fallback (nunca el
 * de axios en inglés). El prefijo "Validación:" es para que el controller elija
 * el status; acá no suma.
 */
export function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    return String(error.response.data.error).replace(/^Validación:\s*/, '');
  }
  return axios.isAxiosError(error) || !(error instanceof Error) ? fallback : error.message;
}

const altasService = {
  async listarPlanes(): Promise<string[]> {
    const response = await axios.get(`${API_URL}/planes`, { headers: authHeaders() });
    return Array.isArray(response.data?.planes) ? response.data.planes : [];
  },

  async listarEjecutivas(): Promise<Ejecutiva[]> {
    const response = await axios.get(`${API_URL}/ejecutivas`, { headers: authHeaders() });
    return Array.isArray(response.data?.ejecutivas) ? response.data.ejecutivas : [];
  },

  async crearEjecutiva(nombre: string): Promise<MutationResponse<Ejecutiva>> {
    try {
      const response = await axios.post(
        `${API_URL}/ejecutivas`,
        { nombre },
        { headers: authHeaders() },
      );
      return response.data;
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Error al crear el ejecutivo') };
    }
  },

  async activarEjecutiva(id: number, activa: boolean): Promise<MutationResponse<Ejecutiva>> {
    try {
      const response = await axios.patch(
        `${API_URL}/ejecutivas/${id}`,
        { activa },
        { headers: authHeaders() },
      );
      return response.data;
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Error al actualizar el ejecutivo') };
    }
  },

  async listarAltas(filtros: FiltrosAltas = {}): Promise<{ altas: Alta[]; total: number }> {
    const params: Record<string, string> = {};
    if (filtros.ejecutivaId) params.ejecutivaId = String(filtros.ejecutivaId);
    if (filtros.estado) params.estado = filtros.estado;
    if (filtros.orden) params.orden = filtros.orden;
    if (filtros.q) params.q = filtros.q;
    if (filtros.limit) {
      params.limit = String(filtros.limit);
      params.offset = String(filtros.offset ?? 0);
    }
    const response = await axios.get(API_URL, { headers: authHeaders(), params });
    return {
      altas: Array.isArray(response.data?.altas) ? response.data.altas : [],
      total: Number(response.data?.total ?? 0),
    };
  },

  async crearAlta(payload: CreateAltaPayload): Promise<MutationResponse<Alta>> {
    try {
      const response = await axios.post(API_URL, payload, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Error al cargar el alta') };
    }
  },

  async importarAltas(ejecutivaId: number, filas: FilaImportar[]): Promise<ImportarResponse> {
    try {
      const response = await axios.post(
        `${API_URL}/importar`,
        { ejecutivaId, filas },
        { headers: authHeaders() },
      );
      return response.data;
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Error al importar las altas') };
    }
  },

  async ventas(): Promise<VentasResponse> {
    const response = await axios.get<VentasResponse>(`${API_URL}/ventas`, { headers: authHeaders() });
    return response.data;
  },

  async ultimaSincronizacion(): Promise<Sincronizacion | null> {
    try {
      const response = await axios.get(`${API_URL}/sync`, { headers: authHeaders() });
      return response.data?.ultima ?? null;
    } catch {
      return null;
    }
  },

  async sincronizar(): Promise<{
    success: boolean;
    error?: string;
    ultima?: Sincronizacion | null;
  }> {
    try {
      const response = await axios.post(`${API_URL}/sync`, {}, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Error al sincronizar con Salesforce') };
    }
  },

  async actualizarAlta(id: number, payload: UpdateAltaPayload): Promise<MutationResponse<Alta>> {
    try {
      const response = await axios.patch(`${API_URL}/${id}`, payload, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Error al actualizar el alta') };
    }
  },

  /**
   * "Calcular ventas": verifica con Salesforce y arma el reporte del período.
   * Con `retryAfter` cuando Salesforce acaba de fallar y hay que esperar (429).
   */
  async calcularVentas(
    desde: string,
    hasta: string,
    ejecutivaId?: number,
  ): Promise<{ success: boolean; error?: string; retryAfter?: number; motivo?: 'limite'; reporte?: ReporteVentas }> {
    try {
      const response = await axios.post(
        `${API_URL}/reporte`,
        { desde: desde || null, hasta: hasta || null, ejecutivaId: ejecutivaId ?? null },
        { headers: authHeaders() },
      );
      const { success, ...reporte } = response.data;
      return { success, reporte: reporte as ReporteVentas };
    } catch (error) {
      const es429 = axios.isAxiosError(error) && error.response?.status === 429;
      const retryAfter = es429 ? Number(error.response?.headers?.['retry-after']) || 30 : undefined;
      // 'limite': demasiados cálculos por minuto, que no es lo mismo que Salesforce caído.
      const motivo = es429 && error.response?.data?.motivo === 'limite' ? ('limite' as const) : undefined;
      return { success: false, error: errorMessage(error, 'No se pudo calcular las ventas'), retryAfter, motivo };
    }
  },

  /**
   * El reporte con lo último conocido, sin consultar Salesforce ni gastar el límite de
   * "Calcular ventas": para refrescar la pantalla después de una decisión del jefe.
   */
  async verReporte(
    desde: string,
    hasta: string,
    ejecutivaId?: number,
  ): Promise<{ success: boolean; error?: string; reporte?: Omit<ReporteVentas, 'verificacion'> }> {
    try {
      const params: Record<string, string> = { desde, hasta };
      if (ejecutivaId) params.ejecutivaId = String(ejecutivaId);
      const response = await axios.get(`${API_URL}/reporte`, { headers: authHeaders(), params });
      const { success, ...reporte } = response.data;
      return { success, reporte: reporte as Omit<ReporteVentas, 'verificacion'> };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'No se pudo refrescar el reporte') };
    }
  },

  /** El reporte en Excel (lo último conocido, sin consultar Salesforce). Devuelve el archivo y su nombre. */
  async descargarReporte(
    desde: string,
    hasta: string,
    ejecutivaId?: number,
  ): Promise<{ success: boolean; error?: string; archivo?: Blob; nombre?: string }> {
    try {
      const params: Record<string, string> = { desde, hasta };
      if (ejecutivaId) params.ejecutivaId = String(ejecutivaId);
      const response = await axios.get(`${API_URL}/reporte.xlsx`, { headers: authHeaders(), params, responseType: 'blob' });
      const disposition = String(response.headers?.['content-disposition'] ?? '');
      const nombre = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `ventas_${desde}_${hasta}.xlsx`;
      return { success: true, archivo: response.data as Blob, nombre };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'No se pudo descargar el Excel') };
    }
  },

  /** Qué tiene ese DNI en Salesforce, para avisar antes de cargarlo. Lanza si Salesforce no responde. */
  async consultarDni(dni: string): Promise<ConsultaDni> {
    const response = await axios.get(`${API_URL}/salesforce/${encodeURIComponent(dni)}`, { headers: authHeaders() });
    return response.data;
  },

  /** El jefe resuelve una venta marcada para revisar. */
  async revisarAlta(id: number, decision: DecisionRevisar): Promise<MutationResponse<Alta>> {
    try {
      const response = await axios.post(`${API_URL}/${id}/revision`, { decision }, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return { success: false, error: errorMessage(error, 'No se pudo guardar la decisión') };
    }
  },

  async borrarAlta(id: number): Promise<MutationResponse<never>> {
    try {
      const response = await axios.delete(`${API_URL}/${id}`, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Error al borrar el alta') };
    }
  },
};

export default altasService;
